import * as _ from 'underscore';
import { appStore } from '../../app/AppStore';
import { selectContentCounts, selectContentCountsUpToDate } from '../../app/selectors/ContentCountsSelector';
import { SessionSiteSelectors } from '../../app/selectors/SessionInfoSelectors';
import { ISpaceInfoTypeItemCount } from '../../app/state/ContentCountsState';
import { UrlParamKeys } from '../../app/UrlParamKeys';
import { Helpers } from '../helpers/Helpers';
import { RefreshPropertyHelper } from '../helpers/RefreshPropertyHelper';
import { L10n } from '../L10n/L10n';
import { DatasourceContentType } from '../models/ContentTypes';
import { IDataSourceDetailInfo } from '../models/IDataSourceInfo';
import { Module } from '../Module';
import { RefreshOptionType } from '../resources/Datasources';
import { IGetPreferredRefreshOptionResult } from '../ServerApi/requests/GetPreferredRefreshOption';
import { RefreshModeTypes } from '../ServerApi/types/IRemoteRefreshProperties';
import { DefaultStateFor } from '../services/ContentUrl';
import { PublishDatasourceAction } from '../services/PublishDatasourceAction';
import { ToasterService, ToastType } from '../services/ToasterService';

export interface IDatasourcePublishResultScope extends ng.IScope {
  datasource: IDataSourceDetailInfo;
  publishResult: IDatasourcePublishResult;
}

export interface IDatasourcePublishResult extends ISyncPublishResult {
  preferredRefreshOption: IGetPreferredRefreshOptionResult;
}

export interface ISyncPublishResult {
  isSyncAvailable: boolean;
  isSyncRunning: boolean;
  remoteAgentWorkflowId: string;
  publishedWithLQS: boolean;
}

export class DatasourcePublishResultCtrl {

  public static $inject = [
    'datasourceDetailInfo',
    'preferredRefreshOption',
    'DatasourcePublishResultService'
  ];

  constructor(
    datasourceDetailInfo: IDataSourceDetailInfo,
    preferredRefreshOption: IGetPreferredRefreshOptionResult,
    datasourcePublishResultService: DatasourcePublishResultService
  ) {
    datasourcePublishResultService.handlePublishedDatasource(datasourceDetailInfo, preferredRefreshOption);
  }
}

Module.instance().controller('DatasourcePublishResultCtrl', DatasourcePublishResultCtrl);

export class DatasourcePublishResultService {
  public static $inject = [
    '$location',
    '$rootScope',
    '$state',
    '$translate',
    'ToasterService',
    'PublishDatasourceAction'
  ];

  constructor(
    private $location: ng.ILocationService,
    private $rootScope: ng.IRootScopeService,
    private $state: ng.ui.IStateService,
    private $translate: ng.translate.ITranslateService,
    private toaster: ToasterService,
    private PublishDatasourceAction: PublishDatasourceAction
  ) {}

  public handlePublishedDatasource(datasourceDetailInfo: IDataSourceDetailInfo, preferredRefreshOption: IGetPreferredRefreshOptionResult) {

    const publishResult: IDatasourcePublishResult = {
      preferredRefreshOption: null,
      isSyncAvailable: null,
      isSyncRunning: null,
      remoteAgentWorkflowId: null,
      publishedWithLQS: null
    };

    publishResult.preferredRefreshOption = preferredRefreshOption;
    const search = this.$location.search();

    switch (preferredRefreshOption.suggestion) {
      case RefreshOptionType.OnlineSync:
        // Before we go, pass along the sync-related query string parameters
        if (_.has(search, UrlParamKeys.SyncAvailable)) {
          publishResult.isSyncAvailable = Helpers.BooleanQueryParamRegex.test(search[UrlParamKeys.SyncAvailable]);
        }
        if (_.has(search, UrlParamKeys.SyncRunning)) {
          publishResult.isSyncRunning = Helpers.BooleanQueryParamRegex.test(search[UrlParamKeys.SyncRunning]);
        }
        if (_.has(search, UrlParamKeys.RemoteAgentWorkflowId)) {
          publishResult.remoteAgentWorkflowId = search[UrlParamKeys.RemoteAgentWorkflowId];
        }
        this.showSuccessDialogAfterStateTransition(publishResult, datasourceDetailInfo);
        break;
      case RefreshOptionType.LiveConnection:
        if (_.has(search, UrlParamKeys.PublishedWithLQS)) {
          publishResult.publishedWithLQS = Helpers.BooleanQueryParamRegex.test(search[UrlParamKeys.PublishedWithLQS]);
        }
        this.showSuccessDialogAfterStateTransition(publishResult, datasourceDetailInfo);
        break;
      case RefreshOptionType.NotSupported:
      case RefreshOptionType.FeatureDisabled:
        this.showSuccessToast(datasourceDetailInfo);
        break;
      case RefreshOptionType.Server:
        this.showSuccessDialogAfterStateTransition(publishResult, datasourceDetailInfo);
      default:
        break;
    }

    if (SessionSiteSelectors.isNLPServiceEnabled(appStore.getState())) {
      this.$state.go(DefaultStateFor(DatasourceContentType.name));
      return;
    }

    switch (preferredRefreshOption.suggestion) {
      case RefreshOptionType.Server:
      case RefreshOptionType.OnlineSync:
        RefreshPropertyHelper.redirectByDatasourceRefreshMode(this.$state, datasourceDetailInfo.refreshMode);
        break;
      case RefreshOptionType.LiveConnection:
      case RefreshOptionType.NotSupported:
      case RefreshOptionType.FeatureDisabled:
      default:
        this.$state.go(DefaultStateFor(DatasourceContentType.name));
        break;
    }
  }

  private showSuccessDialogAfterStateTransition(publishResult: IDatasourcePublishResult, datasourceDetailInfo: IDataSourceDetailInfo) {
    // If we open the modal now and then initiate a state change, the modal will not open properly.
    // So, we'll wait for the state to finish changing and then open the modal
    const unbindEvent = this.$rootScope.$on('$locationChangeSuccess', () => {
      if (publishResult.preferredRefreshOption.suggestion === RefreshOptionType.Server || publishResult.preferredRefreshOption.suggestion === RefreshOptionType.OnlineSync) {
        // We need to get the existing scheduled refreshes count for this data source
        // Wait until the content counts are loaded in the redux store.
        const handlePublishDatasource = () => {
          const appState = appStore.getState();
          const countsUpToDate = selectContentCountsUpToDate(appState);
          const contentCounts: ISpaceInfoTypeItemCount<'datasource'> = selectContentCounts<'datasource'>(appState, 'datasource');
          if (countsUpToDate) {
            if (datasourceDetailInfo.refreshMode !== RefreshModeTypes.Remote) {
              this.PublishDatasourceAction.handlePublishedDatasource(contentCounts['extractRefreshes'], publishResult, datasourceDetailInfo);
            } else {
              this.PublishDatasourceAction.handlePublishedDatasource(contentCounts['remoteRefreshSchedules'], publishResult, datasourceDetailInfo);
            }
            unbindEvent();
          } else {
            setTimeout(handlePublishDatasource, 250);
          }
        };
        handlePublishDatasource();
      } else {
        // For live refresh option
        this.PublishDatasourceAction.handlePublishedDatasource(0, publishResult, datasourceDetailInfo);
        unbindEvent();
      }
    });
  }

  private showSuccessToast(datasourceDetailInfo: IDataSourceDetailInfo) {
    this.toaster.pop({
      type: ToastType.Info,
      text: this.$translate.instant('datasourcePublishSuccess', {
        DATASOURCE_NAME: L10n.elideIfLong(datasourceDetailInfo.name)
      })
    });
  }
}

Module.instance().service('DatasourcePublishResultService', DatasourcePublishResultService);
